/**
 * ===========================================================================
 * ONE APP, MANY FACES — the device-mode router
 * ===========================================================================
 *
 * The product spec forbids a separate KDS app, a separate bar app and a
 * separate customer app. There is ONE PWA — this one — and it renders a
 * completely different shell depending on what the device is for. A cook never
 * sees a floor map; a cashier never sees a "hết nguyên liệu" button; a guest
 * never sees anything but their own table's menu.
 *
 * ---------------------------------------------------------------------------
 * HOW A MODE IS RESOLVED  (first match wins)
 * ---------------------------------------------------------------------------
 *
 *   0. URL              — `/c/:token` is CUSTOMER_ORDER, full stop. It is
 *                         reached by scanning a QR code, never by logging in,
 *                         and it must work before (and without) any staff
 *                         session existing on the device.
 *
 *   1. REGISTERED DEVICE — `devices.device_mode`, set by an admin when the
 *                         device row was created. This is the authoritative
 *                         answer and the one the spec asks for.
 *
 *                         Read via `GET /v1/devices/me` (routes/qr.ts), which
 *                         returns the session's OWN device row and is
 *                         deliberately NOT behind `device.view` — the old
 *                         contract gap (cashier/kitchen roles being unable to
 *                         learn their terminal's mode) is closed. A 404 (no
 *                         device bound) or any failure falls through to (2);
 *                         the mode cached at activation covers offline boots.
 *
 *   2. LOCAL OVERRIDE   — what the operator picked on the activation screen,
 *                         stored in localStorage. Also how a manager flips a
 *                         spare tablet from BAR_DISPLAY to SERVICE_TERMINAL
 *                         during a rush without calling head office.
 *
 *   3. PERMISSIONS      — the last resort, derived from `/auth/me.permissions`.
 *                         A user who can only do `kitchen.*` gets the KDS; a
 *                         user who can take money gets the cashier shell. This
 *                         is a fallback, not a security boundary: the real
 *                         boundary is the API's permission check on every call.
 *                         Rendering a screen a user cannot use is a UX bug;
 *                         hiding one they can is a support call. Both are
 *                         better than trusting the client.
 *
 * Adding a mode = one entry in `DEVICE_MODES`, one branch in `ModeShell`, one
 * inference rule below. Nothing else in the app switches on the mode.
 */

/** The modes this app can wear. Mirrors `devices.device_mode` minus STAFF_MOBILE. */
export const DEVICE_MODES = [
  'POS_CASHIER',
  'SERVICE_TERMINAL',
  'KITCHEN_DISPLAY',
  'BAR_DISPLAY',
  'WAREHOUSE_TERMINAL',
  'QUEUE_DISPLAY',
  'MANAGER_TERMINAL',
  'CUSTOMER_ORDER',
] as const;

export type DeviceMode = (typeof DEVICE_MODES)[number];

/**
 * `STAFF_MOBILE` exists in the DB enum but belongs to the Staff App, not here.
 * A device registered as STAFF_MOBILE that somehow opens the POS is treated as
 * a SERVICE_TERMINAL rather than crashing — the modes overlap enough that a
 * waiter can work.
 */
export function normalizeWireMode(wire: string | null | undefined): DeviceMode | null {
  if (wire === null || wire === undefined) return null;
  if (wire === 'STAFF_MOBILE') return 'SERVICE_TERMINAL';
  return (DEVICE_MODES as readonly string[]).includes(wire) ? (wire as DeviceMode) : null;
}

export interface DeviceModeInfo {
  readonly mode: DeviceMode;
  readonly nameVi: string;
  readonly descriptionVi: string;
  /** Permissions that make this mode useful. Used by inference and the picker. */
  readonly signals: readonly string[];
  /** Lower wins when two modes are equally plausible. */
  readonly priority: number;
  /** Modes that must never be reached by logging in. */
  readonly urlOnly?: boolean;
}

export const DEVICE_MODE_INFO: Readonly<Record<DeviceMode, DeviceModeInfo>> = {
  POS_CASHIER: {
    mode: 'POS_CASHIER',
    nameVi: 'Thu ngân',
    descriptionVi: 'Sơ đồ bàn, gọi món, thanh toán, in hóa đơn, ca quỹ.',
    signals: ['payment.receive', 'cash.session.open', 'invoice.print'],
    priority: 10,
  },
  SERVICE_TERMINAL: {
    mode: 'SERVICE_TERMINAL',
    nameVi: 'Phục vụ',
    descriptionVi: 'Bàn của tôi, khách gọi nhân viên, duyệt đơn khách, nhận & trả món, dọn bàn.',
    signals: ['order.confirm', 'table.clean', 'table.setup', 'task.accept'],
    priority: 20,
  },
  KITCHEN_DISPLAY: {
    mode: 'KITCHEN_DISPLAY',
    nameVi: 'Màn hình bếp (KDS)',
    descriptionVi: 'Bảng phiếu chế biến theo trạng thái, cảnh báo trễ SLA, dị ứng.',
    signals: ['kitchen.view', 'kitchen.accept', 'kitchen.complete'],
    priority: 30,
  },
  BAR_DISPLAY: {
    mode: 'BAR_DISPLAY',
    nameVi: 'Màn hình pha chế (Bar)',
    descriptionVi: 'Cùng engine với KDS, hiển thị đá / đường / size / topping.',
    signals: ['bar.view', 'bar.accept', 'bar.complete'],
    priority: 40,
  },
  WAREHOUSE_TERMINAL: {
    mode: 'WAREHOUSE_TERMINAL',
    nameVi: 'Kho',
    descriptionVi: 'Nhận hàng, kiểm kê, xuất nguyên liệu, báo hao hụt.',
    signals: ['inventory.receive', 'inventory.count', 'inventory.issue', 'inventory.wastage'],
    priority: 50,
  },
  QUEUE_DISPLAY: {
    mode: 'QUEUE_DISPLAY',
    nameVi: 'Bảng gọi món (mang về)',
    descriptionVi: 'Màn hình lớn hiển thị số đơn đã sẵn sàng cho khách mang về.',
    signals: ['kitchen.view', 'bar.view', 'order.view'],
    priority: 60,
  },
  MANAGER_TERMINAL: {
    mode: 'MANAGER_TERMINAL',
    nameVi: 'Quản lý ca',
    descriptionVi: 'Tổng quan chi nhánh theo thời gian thực và hộp thư duyệt thao tác nhạy cảm.',
    signals: ['report.revenue.view', 'kpi.view.branch', 'alert.view', 'order.discount.override'],
    priority: 15,
  },
  CUSTOMER_ORDER: {
    mode: 'CUSTOMER_ORDER',
    nameVi: 'Khách quét QR',
    descriptionVi: 'Trang gọi món đa ngôn ngữ cho khách. Vào bằng đường dẫn /c/:token, không đăng nhập.',
    signals: [],
    priority: 999,
    urlOnly: true,
  },
};

/** Modes an operator may pick on the activation screen. */
export const SELECTABLE_MODES: readonly DeviceMode[] = DEVICE_MODES.filter(
  (m) => DEVICE_MODE_INFO[m].urlOnly !== true,
);

export type ModeSource = 'URL' | 'REGISTERED_DEVICE' | 'LOCAL_OVERRIDE' | 'PERMISSIONS' | 'DEFAULT';

export interface ResolvedMode {
  readonly mode: DeviceMode;
  readonly source: ModeSource;
  /** Station board to show, for KITCHEN_DISPLAY / BAR_DISPLAY. */
  readonly stationId: string | null;
}

export interface ResolveModeInput {
  /** `device_mode` from the API, when the session is allowed to read it. */
  readonly registeredMode?: string | null;
  readonly registeredStationId?: string | null;
  /** What the operator chose at activation (localStorage). */
  readonly localMode?: string | null;
  readonly localStationId?: string | null;
  /** `/auth/me.permissions`, effective for the active branch. */
  readonly permissions?: readonly string[];
  /** `/auth/me.session.activeStationId`. */
  readonly sessionStationId?: string | null;
}

/**
 * Infer a mode from what the user is allowed to do.
 *
 * Scoring, not first-match: a shift leader holds cashier AND kitchen AND report
 * permissions, and "first rule that matches" would drop them wherever the rules
 * happened to be ordered. Each mode scores one point per signal it holds, ties
 * break on `priority`, and a mode with zero signals is never chosen.
 */
export function inferModeFromPermissions(permissions: readonly string[]): DeviceMode | null {
  const held = new Set(permissions);
  let best: { mode: DeviceMode; score: number; priority: number } | null = null;

  for (const mode of SELECTABLE_MODES) {
    const info = DEVICE_MODE_INFO[mode];
    const score = info.signals.reduce((acc, code) => acc + (held.has(code) ? 1 : 0), 0);
    if (score === 0) continue;
    if (best === null || score > best.score || (score === best.score && info.priority < best.priority)) {
      best = { mode, score, priority: info.priority };
    }
  }
  return best?.mode ?? null;
}

/**
 * THE resolution function. Pure, so it is unit-testable and so the same answer
 * is reached on first paint and after a token refresh.
 *
 * CUSTOMER_ORDER is absent here by design: it is decided by the router before
 * any of this runs, because it must work with no session at all.
 */
export function resolveDeviceMode(input: ResolveModeInput): ResolvedMode {
  const registered = normalizeWireMode(input.registeredMode);
  if (registered !== null && registered !== 'CUSTOMER_ORDER') {
    return {
      mode: registered,
      source: 'REGISTERED_DEVICE',
      stationId: input.registeredStationId ?? input.sessionStationId ?? input.localStationId ?? null,
    };
  }

  const local = normalizeWireMode(input.localMode);
  if (local !== null && local !== 'CUSTOMER_ORDER') {
    return {
      mode: local,
      source: 'LOCAL_OVERRIDE',
      stationId: input.localStationId ?? input.sessionStationId ?? null,
    };
  }

  const inferred = inferModeFromPermissions(input.permissions ?? []);
  if (inferred !== null) {
    return { mode: inferred, source: 'PERMISSIONS', stationId: input.sessionStationId ?? null };
  }

  // Nothing matched: a signed-in user with no recognisable permissions. The
  // service terminal is the least destructive shell — it can read and it can
  // ask for help, but it cannot take money or void anything.
  return { mode: 'SERVICE_TERMINAL', source: 'DEFAULT', stationId: input.sessionStationId ?? null };
}

/** Is this mode a production board? (KDS and Bar share one engine.) */
export function isProductionMode(mode: DeviceMode): boolean {
  return mode === 'KITCHEN_DISPLAY' || mode === 'BAR_DISPLAY';
}

/** Which permission family a production board acts under. */
export function productionDomainFor(mode: DeviceMode): 'kitchen' | 'bar' {
  return mode === 'BAR_DISPLAY' ? 'bar' : 'kitchen';
}
